import { useMemo } from 'react';
import { useLocation, useResolvedPath } from 'react-router-dom';
import {
  BreadcrumbEntry,
  PageLayout,
  createFrontendModule,
  useBreadcrumbEntries,
} from '@backstage/frontend-plugin-api';
import type { PageLayoutProps } from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import { Header, PluginHeader } from '@backstage/ui';
import { makeStyles } from '@material-ui/core/styles';

// Hide the default title when the page renders its own (Backstage UI or legacy header).
const OWN_HEADER = [
  '[class*="bui-HeaderContent"]',
  '[data-backstage-core-header]',
];
const hasOwnHeader = OWN_HEADER.flatMap(selector => [
  `~ ${selector}`,
  `~ * ${selector}`,
]).join(', ');

// With tabs the default header stays (tabs sit under the title), but its title row is hidden.
const useStyles = makeStyles({
  '@global': {
    [`[data-essity-default-title]:not([data-has-tabs]):has(${hasOwnHeader})`]: {
      display: 'none',
    },
    [`[data-essity-default-title][data-has-tabs]:has(${hasOwnHeader}) [class*="bui-HeaderContent"]`]:
      { display: 'none' },
  },
});

// Same as the default Backstage page layout, plus a page title under the plugin header.
export const EssityPageLayout = (props: PageLayoutProps) => {
  const { title, icon, noHeader, titleLink, headerActions, tabs, children } =
    props;
  useStyles();
  const parentPath = useResolvedPath('.').pathname.replace(/\/$/, '');
  const resolvedTabs = useMemo(
    () =>
      tabs?.map(tab => ({
        ...tab,
        href: tab.href.startsWith('/')
          ? tab.href
          : `${parentPath}/${tab.href}`.replace(/\/{2,}/g, '/'),
        matchStrategy: 'prefix' as const,
      })),
    [tabs, parentPath],
  );
  const { pathname } = useLocation();
  const activeTabId =
    resolvedTabs
      ?.filter(
        tab => pathname === tab.href || pathname.startsWith(`${tab.href}/`),
      )
      .sort((a, b) => b.href.length - a.href.length)[0]?.id ?? null;
  const { items: breadcrumbs } = useBreadcrumbEntries();

  if (noHeader) {
    return <>{children}</>;
  }

  const content = (
    <>
      <PluginHeader
        title={title}
        icon={icon}
        titleLink={titleLink}
        breadcrumbs={breadcrumbs}
        customActions={headerActions}
      />
      {(title || resolvedTabs?.length) && (
        <div
          data-essity-default-title
          data-has-tabs={resolvedTabs?.length ? '' : undefined}
        >
          <Header
            title={title}
            tabs={resolvedTabs?.map(({ id, label, href }) => ({
              id,
              label,
              href,
            }))}
            activeTabId={activeTabId}
          />
        </div>
      )}
      {children}
    </>
  );

  if (!title) {
    return content;
  }
  return (
    <BreadcrumbEntry
      entry={{ label: title, href: titleLink ?? (parentPath || '/') }}
    >
      {content}
    </BreadcrumbEntry>
  );
};

export const pageLayoutModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SwappableComponentBlueprint.make({
      name: 'core-page-layout',
      params: define =>
        define({
          component: PageLayout,
          loader: () => EssityPageLayout,
        }),
    }),
  ],
});
